/**
 * Pure client-side validation for the registration screen (Req 22.1, 22.2).
 *
 * The registration screen must BLOCK submission and flag EACH invalid field
 * with a User_Message when the email is empty/invalid or the password is empty
 * (Req 22.2). This module holds that rule as a small, pure, side-effect-free
 * function so it can be exercised directly by the property/component tests
 * (task 15.2) without rendering a component or standing up a backend.
 *
 * The function returns, per field, the message-catalog id of the User_Message
 * to show (or `null` when the field is valid). It never resolves the id to text
 * itself — that stays with the i18n resolver (`useT`) at render time (Req 22.11)
 * — so the helper is locale-agnostic and deterministic.
 *
 * "Blocks submission" is expressed by {@link isRegistrationValid}: submission is
 * permitted only when every field error is `null`.
 */

import type { KnownMessageId } from "../i18n/messages.js";

/**
 * Per-field validation result for the registration form. A `null` value means
 * the field is valid; a non-null value is the catalog id of the field-level
 * User_Message to render (Req 22.2). Using catalog ids (not raw text) keeps the
 * result translatable and lets tests assert on stable ids.
 */
export interface RegistrationFieldErrors {
  /**
   * `auth.register.emailEmpty` when the email is empty/whitespace,
   * `auth.register.emailInvalid` when it is present but not a valid address,
   * otherwise `null`.
   */
  readonly email:
    | "auth.register.emailEmpty"
    | "auth.register.emailInvalid"
    | null;
  /** `auth.register.passwordEmpty` when the password is empty, otherwise `null`. */
  readonly password: "auth.register.passwordEmpty" | null;
}

/**
 * A pragmatic email shape check: a single `@` with non-empty, whitespace-free
 * local and domain parts and at least one dot in the domain. This is a
 * client-side "flag obviously invalid input" guard (Req 22.2), not an
 * authoritative validator — the Identity_Service remains the source of truth on
 * submit. Kept deliberately simple and anchored so it is deterministic across
 * server and client renders.
 */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Validate the registration inputs client-side (Req 22.2).
 *
 * Rules, evaluated per field so EACH invalid field is flagged independently:
 *   - email empty/whitespace  -> `auth.register.emailEmpty`
 *   - email present but bad    -> `auth.register.emailInvalid`
 *   - password empty           -> `auth.register.passwordEmpty`
 *
 * Pure: no trimming mutation of inputs, no I/O, no globals — safe for SSR and
 * for property testing across arbitrary strings.
 */
export function validateRegistration(
  email: string,
  password: string,
): RegistrationFieldErrors {
  return {
    email: validateEmail(email),
    password: password === "" ? "auth.register.passwordEmpty" : null,
  };
}

/** Validate just the email field, returning its catalog id or `null`. */
function validateEmail(
  email: string,
): RegistrationFieldErrors["email"] {
  if (email.trim() === "") {
    return "auth.register.emailEmpty";
  }
  if (!EMAIL_PATTERN.test(email)) {
    return "auth.register.emailInvalid";
  }
  return null;
}

/**
 * Whether the registration form may be submitted: true only when every field
 * error is `null`. Screens call this to BLOCK submission on any invalid field
 * (Req 22.2).
 */
export function isRegistrationValid(errors: RegistrationFieldErrors): boolean {
  return errors.email === null && errors.password === null;
}

/**
 * The catalog ids this helper can produce, as a compile-time-checked subset of
 * {@link KnownMessageId}. Exported so consumers (and tests) can reason about the
 * full set of field-error ids without duplicating the literals. If any id here
 * is not present in the message catalog, this line is a type error.
 */
export const REGISTRATION_FIELD_ERROR_IDS: readonly KnownMessageId[] = [
  "auth.register.emailEmpty",
  "auth.register.emailInvalid",
  "auth.register.passwordEmpty",
] as const;
